import { useMemo } from 'react';
import { IMPACT_META, SESSIONS, type MarketEvent } from '@/mocks/marketCalendar';
import type { EventStatus } from '../useMarketClock';

const HOUR_TICKS = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22];

function sessionSegments(start: number, end: number): { left: number; width: number }[] {
  if (start < end) {
    return [{ left: start, width: end - start }];
  }
  // wraps midnight
  return [
    { left: start, width: 1440 - start },
    { left: 0, width: end },
  ];
}

export default function Timeline24h({
  events,
  nowDisplayMinutes,
  toDisplay,
  statusOf,
  onSelect,
}: {
  events: MarketEvent[];
  nowDisplayMinutes: number;
  toDisplay: (utc: number) => number;
  statusOf: (e: MarketEvent) => EventStatus;
  onSelect: (e: MarketEvent) => void;
}) {
  const sessions = useMemo(() => {
    return SESSIONS.map((s) => {
      const start = toDisplay(s.startUtc);
      const end = toDisplay(s.endUtc);
      return { ...s, segments: sessionSegments(start, end) };
    });
  }, [toDisplay]);

  const nowPct = (nowDisplayMinutes / 1440) * 100;

  const highEvents = events.filter((e) => e.impact === 'high');
  const otherEvents = events.filter((e) => e.impact !== 'high');

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[820px]">
        {/* session bands */}
        <div className="relative h-7 w-full overflow-hidden rounded-t-xl bg-slate-50">
          {sessions.map((s) =>
            s.segments.map((seg, i) => (
              <span
                key={`${s.id}-${i}`}
                className="absolute top-0 flex h-full items-center justify-center text-[10px] font-semibold text-white"
                style={{
                  left: `${(seg.left / 1440) * 100}%`,
                  width: `${(seg.width / 1440) * 100}%`,
                  backgroundColor: s.color,
                  opacity: 0.85,
                }}
              >
                {seg.width > 90 ? s.name : ''}
              </span>
            )),
          )}
        </div>

        {/* timeline body */}
        <div className="relative h-40 border-x border-b border-slate-200/80 bg-white">
          {/* hour grid */}
          {Array.from({ length: 25 }).map((_, i) => {
            const pct = (i / 24) * 100;
            const major = i % 2 === 0;
            return (
              <div key={i} className="absolute top-0 h-full" style={{ left: `${pct}%` }}>
                <div className={`w-px ${major ? 'bg-slate-200' : 'bg-slate-100'}`} style={{ height: '100%' }} />
              </div>
            );
          })}

          {/* hour labels */}
          {HOUR_TICKS.map((h) => (
            <span
              key={h}
              className="absolute -translate-x-1/2 font-mono text-[10px] text-slate-400"
              style={{ left: `${(h / 24) * 100}%`, bottom: 4 }}
            >
              {String(h).padStart(2, '0')}
            </span>
          ))}

          {/* high impact nodes with labels */}
          {highEvents.map((e) => {
            const pct = (toDisplay((parseInt(e.time.slice(0, 2), 10)) * 60 + parseInt(e.time.slice(3, 5), 10)) / 1440) * 100;
            const meta = IMPACT_META[e.impact];
            const status = statusOf(e);
            return (
              <button
                key={e.id}
                type="button"
                onClick={() => onSelect(e)}
                className="group absolute -translate-x-1/2 text-center"
                style={{ left: `${pct}%`, top: 16 }}
                title={`${e.title} (${e.time} UTC)`}
              >
                <span
                  className="mx-auto flex items-center justify-center rounded-full ring-4 ring-white transition-transform group-hover:scale-110"
                  style={{ backgroundColor: meta.color, width: 18, height: 18 }}
                >
                  {status === 'released' && <span className="text-[9px] text-white">✓</span>}
                </span>
                <span className="mt-1 block whitespace-nowrap rounded-md bg-slate-900 px-1.5 py-0.5 text-[9px] font-semibold text-white">
                  {e.time}
                </span>
              </button>
            );
          })}

          {/* other impact nodes (smaller dots) */}
          {otherEvents.map((e, idx) => {
            const pct = (toDisplay((parseInt(e.time.slice(0, 2), 10)) * 60 + parseInt(e.time.slice(3, 5), 10)) / 1440) * 100;
            const meta = IMPACT_META[e.impact];
            return (
              <button
                key={e.id}
                type="button"
                onClick={() => onSelect(e)}
                className="group absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${pct}%`, top: 86 + (idx % 3) * 16 }}
                title={`${e.title} (${e.time} UTC)`}
              >
                <span
                  className="block rounded-full ring-2 ring-white transition-transform group-hover:scale-125"
                  style={{ backgroundColor: meta.color, width: 8, height: 8, opacity: 0.75 }}
                />
              </button>
            );
          })}

          {/* NOW pointer */}
          <div className="pointer-events-none absolute top-0 h-full" style={{ left: `${nowPct}%` }}>
            <div className="mx-auto flex h-full w-px flex-col items-center bg-violet-600">
              <span className="rounded-b-md bg-violet-600 px-1.5 py-0.5 font-mono text-[9px] font-bold text-white">
                NOW
              </span>
              <div className="w-px flex-1 bg-violet-600" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}